import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { UsersIcon, ClockIcon, CheckCircleIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon } from 'lucide-react';
export function OperationalAnalyticsReport({
  filters,
  locale,
  report
}) {
  // Sample data for user productivity
  const userData = [{
    id: 'USR-001',
    name: 'John Smith',
    department: 'Finance',
    tasksCompleted: 145,
    avgProcessingTime: 2.3,
    exceptionRate: 3.2
  }, {
    id: 'USR-002',
    name: 'Sarah Johnson',
    department: 'Procurement',
    tasksCompleted: 178,
    avgProcessingTime: 1.8,
    exceptionRate: 2.1
  }, {
    id: 'USR-003',
    name: 'Michael Brown',
    department: 'Accounts Payable',
    tasksCompleted: 132,
    avgProcessingTime: 2.5,
    exceptionRate: 4.7
  }, {
    id: 'USR-004',
    name: 'Emily Davis',
    department: 'Finance',
    tasksCompleted: 156,
    avgProcessingTime: 2.0,
    exceptionRate: 2.8
  }, {
    id: 'USR-005',
    name: 'Robert Wilson',
    department: 'Procurement',
    tasksCompleted: 112,
    avgProcessingTime: 3.1,
    exceptionRate: 5.2
  }];
  // Processing time trend data
  const processingTimeTrendData = [{
    month: 'Jan',
    time: 3.2
  }, {
    month: 'Feb',
    time: 2.8
  }, {
    month: 'Mar',
    time: 2.5
  }, {
    month: 'Apr',
    time: 2.2
  }, {
    month: 'May',
    time: 1.9
  }, {
    month: 'Jun',
    time: 1.8
  }];
  // Document processing by type
  const documentProcessingData = [{
    type: 'Invoices',
    count: 280
  }, {
    type: 'Purchase Orders',
    count: 175
  }, {
    type: 'Requisitions',
    count: 120
  }, {
    type: 'Receipts',
    count: 95
  }, {
    type: 'Contracts',
    count: 45
  }];
  // System usage by module
  const moduleUsageData = [{
    module: 'AP',
    users: 42,
    transactions: 320
  }, {
    module: 'Procurement',
    users: 38,
    transactions: 280
  }, {
    module: 'Vendors',
    users: 25,
    transactions: 150
  }, {
    module: 'Reporting',
    users: 35,
    transactions: 210
  }, {
    module: 'Settings',
    users: 12,
    transactions: 45
  }];
  // Use report title and description if provided, otherwise use defaults
  const reportTitle = report?.name || 'User Productivity Report';
  const reportDescription = report?.description || 'Analysis of user productivity, process efficiency, and system performance';
  return <div className="p-6" data-id="element-13493">
      <div className="mb-8" data-id="element-13494">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-13495">{reportTitle}</h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-13496">{reportDescription}</p>
      </div>
      {/* Key metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-13497">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-13498">
          <div className="p-5" data-id="element-13499">
            <div className="flex items-center" data-id="element-13500">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-13501">
                <UsersIcon className="h-6 w-6 text-white" data-id="element-13502" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-13503">
                <dl data-id="element-13504">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13505">
                    Active Users
                  </dt>
                  <dd data-id="element-13506">
                    <div className="text-lg font-medium text-gray-900" data-id="element-13507">42</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-13508">
            <div className="text-sm" data-id="element-13509">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-13510">
                View all users
              </a>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-13511">
          <div className="p-5" data-id="element-13512">
            <div className="flex items-center" data-id="element-13513">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-13514">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-13515" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-13516">
                <dl data-id="element-13517">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13518">
                    Avg. Processing Time
                  </dt>
                  <dd data-id="element-13519">
                    <div className="text-lg font-medium text-gray-900" data-id="element-13520">
                      1.8 days
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-13521">
            <div className="text-sm" data-id="element-13522">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-13523">
                View processing details
              </a>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-13524">
          <div className="p-5" data-id="element-13525">
            <div className="flex items-center" data-id="element-13526">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-13527">
                <CheckCircleIcon className="h-6 w-6 text-white" data-id="element-13528" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-13529">
                <dl data-id="element-13530">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-13531">
                    System Uptime
                  </dt>
                  <dd data-id="element-13532">
                    <div className="text-lg font-medium text-gray-900" data-id="element-13533">
                      99.97%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3" data-id="element-13534">
            <div className="text-sm" data-id="element-13535">
              <a href="#" className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-13536">
                View system status
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-13537">
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-13538">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13539">
            Document Processing by Type
          </h4>
          <div className="h-80" data-id="element-13540">
            <ResponsiveContainer width="100%" height="100%" data-id="element-13541">
              <BarChart data={documentProcessingData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-13542">
                <CartesianGrid strokeDasharray="3 3" data-id="element-13543" />
                <XAxis dataKey="type" data-id="element-13544" />
                <YAxis data-id="element-13545" />
                <Tooltip data-id="element-13546" />
                <Bar dataKey="count" fill="#4f46e5" data-id="element-13547" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow" data-id="element-13548">
          <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-13549">
            Average Processing Time Trend (Days)
          </h4>
          <div className="h-80" data-id="element-13550">
            <ResponsiveContainer width="100%" height="100%" data-id="element-13551">
              <LineChart data={processingTimeTrendData} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-13552">
                <CartesianGrid strokeDasharray="3 3" data-id="element-13553" />
                <XAxis dataKey="month" data-id="element-13554" />
                <YAxis data-id="element-13555" />
                <Tooltip data-id="element-13556" />
                <Line type="monotone" dataKey="time" stroke="#10b981" activeDot={{
                r: 8
              }} data-id="element-13557" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* User Productivity Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-13558">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-13559">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13560">
            User Productivity Metrics
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-13561">
            Performance metrics by user for the selected time period
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-13562">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-13563">
            <thead className="bg-gray-50" data-id="element-13564">
              <tr data-id="element-13565">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13566">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13567">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13568">
                  Tasks Completed
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13569">
                  Avg. Processing Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13570">
                  Exception Rate
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13571">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-13572">
              {userData.map(user => <tr key={user.id} data-id="element-13573">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-13574">
                    {user.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13575">
                    {user.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13576">
                    {user.tasksCompleted}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13577">
                    {user.avgProcessingTime} days
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13578">
                    {user.exceptionRate}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-13579">
                    <div className="flex items-center" data-id="element-13580">
                      {user.avgProcessingTime < 2.0 ? <TrendingUpIcon className="h-5 w-5 text-green-500 mr-1.5" data-id="element-13581" /> : user.avgProcessingTime < 2.5 ? <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-1.5" data-id="element-13582" /> : <AlertCircleIcon className="h-5 w-5 text-yellow-500 mr-1.5" data-id="element-13583" />}
                      <span className={`text-sm font-medium ${user.avgProcessingTime < 2.0 ? 'text-green-700' : user.avgProcessingTime < 2.5 ? 'text-blue-700' : 'text-yellow-700'}`} data-id="element-13584">
                        {user.avgProcessingTime < 2.0 ? 'Excellent' : user.avgProcessingTime < 2.5 ? 'Good' : 'Needs Improvement'}
                      </span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}